import { DirectReportDto } from "@coaching-culture/types";
import { useGoalTerminology } from "auth/OrgProvider";
import { fromUnixTime, getUnixTime, isFuture, isToday } from "date-fns";
import { sortBy } from "lodash";
import { measuresLastReviewed } from "./metrics";

export type ActivityKind = "conversation" | "goal" | "feedback" | "measures";

export type ActivityTone = "good" | "muted" | "warning" | "plain";

export type ActivityEvent = {
  id: string;
  kind: ActivityKind;
  title: string;
  detail: string;
  /** Unix seconds */
  date: number;
  tone: ActivityTone;
  href?: string;
};

type Terms = ReturnType<typeof useGoalTerminology>;

export const conversationHref = (x: { id: string; index: number }) =>
  `/solutions/performance/conversations/${x.id}/${x.index}`;

const isUpcoming = (x: DirectReportDto["conversations"][number]) => {
  if (x.completedOn != null || x.cancelledOn != null) return false;
  const d = fromUnixTime(x.scheduledDate);
  return isFuture(d) || isToday(d);
};

/** Conversations still to happen, soonest first. */
export const upcomingConversations = (user: DirectReportDto) =>
  sortBy(user.conversations.filter(isUpcoming), (x) => x.scheduledDate);

/**
 * What has happened with a team member, newest first: past conversations,
 * completed and overdue goals, feedback received and the last measures
 * review. Built from the data the team page already loads.
 */
export const pastActivity = (
  user: DirectReportDto,
  terms: Terms,
): ActivityEvent[] => {
  const now = getUnixTime(new Date());
  const goal = terms.goal.asTitle();
  const events: ActivityEvent[] = [];

  for (const c of user.conversations) {
    if (isUpcoming(c)) continue;
    const base = {
      id: `c-${c.id}-${c.index}`,
      kind: "conversation" as const,
      detail: c.purpose,
      date: c.scheduledDate,
      href: conversationHref(c),
    };
    if (c.cancelledOn != null) {
      events.push({ ...base, title: `${c.type} cancelled`, tone: "muted" });
    } else if (c.completedOn != null) {
      events.push({ ...base, title: `${c.type} completed`, tone: "good" });
    } else {
      events.push({
        ...base,
        title: `${c.type} not marked complete`,
        tone: "warning",
      });
    }
  }

  for (const g of user.goals) {
    if (g.completedOn != null) {
      events.push({
        id: `g-${g.id}`,
        kind: "goal",
        title: `${goal} completed`,
        detail: g.purpose,
        date: g.completedOn,
        tone: "good",
      });
    } else if (g.deadline != null && g.deadline < now) {
      events.push({
        id: `g-${g.id}`,
        kind: "goal",
        title: `${goal} overdue`,
        detail: g.purpose,
        date: g.deadline,
        tone: "warning",
      });
    }
  }

  for (const f of user.receivedFeedback) {
    events.push({
      id: `f-${f.id}`,
      kind: "feedback",
      title: `${f.type === "praise" ? "Praise" : "Feedback"} from ${f.sentBy.name}`,
      detail: f.summary,
      date: f.sentOn,
      tone: "plain",
    });
  }

  const reviewed = measuresLastReviewed(user);
  if (reviewed != null) {
    const count = user.measures.filter((x) => x.lastSet === reviewed).length;
    events.push({
      id: "measures",
      kind: "measures",
      title: "Measures reviewed",
      detail: `${count} ${count === 1 ? "measure" : "measures"} updated`,
      date: reviewed,
      tone: "plain",
    });
  }

  return sortBy(events, (x) => -x.date);
};
